import React, { useEffect, useMemo, useState } from 'react';
import { Field, FieldArray, useFormikContext } from 'formik';
import { Add, Delete } from '@mui/icons-material';
import { Typography } from '@material-tailwind/react';

import {
  AutoCompleteSelectUI,
  ButtonUI,
  CheckboxUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
  SelectUI,
} from '../../Interface';
import { BiFieldType, BiWritebackInputTypeList } from '../../../config/bi_report';
import biWritebackService from '../../../service/biWriteback.service';

const BiWritebackConfiguration = ({
  index,
  remove,
  tableOptions,
  reportFieldOptions,
  defaultField,
}) => {
  const { errors, setFieldValue, validateField, values } = useFormikContext();
  const writeback = values.biWriteback[index];
  const writebackErrors = errors.biWriteback?.[index];
  const [targetFieldOptions, setTargetFieldOptions] = useState([]);
  const configuredTable = tableOptions.find((table) => table.tableName === writeback?.tableName);

  useEffect(() => {
    if (!configuredTable) {
      setTargetFieldOptions([]);
      return;
    }

    let isCurrentRequest = true;
    biWritebackService
      .getTableFields({
        tableName: writeback.tableName,
      })
      .then((data) => {
        if (!isCurrentRequest) return;
        setTargetFieldOptions(
          data.map((field) => ({
            value: field.fieldName,
            label: field.fieldName,
          }))
        );
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [configuredTable, writeback?.tableName]);

  const selectedTableValue = writeback?.tableName ?? null;
  const selectedTableLabel = tableOptions.find((table) => table.value === selectedTableValue)?.label ?? '';
  const configuredKeySignature = (configuredTable?.keyFields ?? []).join('::');
  const currentKeySignature = writeback.keys.map((key) => key.targetField).join('::');
  const editableTargetFieldOptions = targetFieldOptions.filter(
    (field) => !writeback.keys.some((key) => key.targetField === field.value)
  );

  const ensureReportField = (sourceField) => {
    if (!sourceField || values.biField.some((field) => field.field === sourceField)) {
      return Promise.resolve();
    }

    return setFieldValue('biField', [
      ...values.biField,
      {
        ...defaultField,
        field: sourceField,
        type: BiFieldType.HIDDEN,
        name: sourceField,
      },
    ]);
  };

  useEffect(() => {
    if (!configuredTable || configuredKeySignature === currentKeySignature) return;

    setFieldValue(
      `biWriteback.${index}.keys`,
      configuredTable.keyFields.map((targetField, keyIndex) => {
        const savedKey = writeback.keys.find((key) => key.targetField === targetField);
        return {
          ...savedKey,
          sourceField: savedKey?.sourceField ?? '',
          targetField,
          order: keyIndex,
        };
      })
    );
  }, [configuredKeySignature, configuredTable, currentKeySignature, index, setFieldValue, writeback.keys]);

  return (
    <div className="mx-4 mb-6 rounded border border-gray-300 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <Typography variant="h6">Szerkesztési mód</Typography>
        <button type="button" onClick={() => remove(index)} className="text-red-600" title="Szerkesztési mód törlése">
          <Delete />
        </button>
      </div>

      <div className="grid grid-cols-12 gap-x-4 mb-5 items-center">
        <div className="col-span-4">
          <Field
            name={`biWriteback.${index}.name`}
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Megnevezés" />}
            variant="standard"
            helperText={<FormErrorUI message={writebackErrors?.name} />}
            InputLabelProps={{ shrink: Boolean(writeback?.name) }}
          />
        </div>
        <div className="col-span-5">
          <Field
            type="autocomplete"
            name={`biWriteback.${index}.tableName`}
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Céltábla" />}
            variant="standard"
            dataset={tableOptions}
            selectedValue={selectedTableValue}
            selectedLabelValue={selectedTableLabel}
            helperText={<FormErrorUI message={writebackErrors?.tableName} />}
            inputLabelProps={{ shrink: Boolean(writeback?.tableName) }}
            onChange={(_event, selectedTable) => {
              setFieldValue(`biWriteback.${index}.tableName`, selectedTable?.tableName ?? '');
              setFieldValue(
                `biWriteback.${index}.keys`,
                (selectedTable?.keyFields ?? []).map((targetField, keyIndex) => {
                  const savedKey = writeback.keys.find((key) => key.targetField === targetField);
                  return {
                    ...savedKey,
                    sourceField: savedKey?.sourceField ?? '',
                    targetField,
                    order: keyIndex,
                  };
                })
              );
              setFieldValue(
                `biWriteback.${index}.fields`,
                writeback.fields.map((field) => ({ ...field, targetField: '' }))
              );
            }}
          />
        </div>
        <div className="col-span-3">
          <Field
            name={`biWriteback.${index}.active`}
            as={CheckboxUI}
            label={<FormLabelUI text="Aktív" />}
            checked={Boolean(writeback?.active)}
            onChange={(event) => setFieldValue(`biWriteback.${index}.active`, event.target.checked)}
          />
        </div>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <Typography variant="h6">Kulcsmezők</Typography>
        <FormErrorUI message={typeof writebackErrors?.keys === 'string' ? writebackErrors.keys : null} />
      </div>
      {writeback.keys.map((key, keyIndex) => (
        <div className="grid grid-cols-12 gap-x-4 mb-4 items-center" key={key.id ?? key.targetField}>
          <div className="col-span-6">
            <Field
              name={`biWriteback.${index}.keys.${keyIndex}.targetField`}
              as={InputUI}
              fullWidth
              label={<FormLabelUI text="Kulcsmező" />}
              variant="standard"
              value={key.targetField}
              disabled
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="col-span-6">
            <Field
              type="autocomplete"
              name={`biWriteback.${index}.keys.${keyIndex}.sourceField`}
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Riportmező" />}
              variant="standard"
              dataset={reportFieldOptions}
              selectedValue={key.sourceField}
              selectedLabelValue={key.sourceField}
              helperText={<FormErrorUI message={writebackErrors?.keys?.[keyIndex]?.sourceField} />}
              onChange={(_event, selectedField) => {
                const sourceField = selectedField?.value ?? '';
                setFieldValue(
                  `biWriteback.${index}.keys.${keyIndex}.sourceField`,
                  sourceField
                )
                  .then(() => ensureReportField(sourceField))
                  .then(() => validateField(`biWriteback.${index}.keys.${keyIndex}.sourceField`));
              }}
            />
          </div>
        </div>
      ))}

      <FieldArray name={`biWriteback.${index}.fields`}>
        {({ push, remove: removeField }) => (
          <>
            <div className="mb-3 mt-5 flex items-center gap-2">
              <Typography variant="h6">Szerkeszthető mezők</Typography>
              <ButtonUI
                text={<Add fontSize="small" />}
                className="bg-success p-0.5"
                type="button"
                onClick={() =>
                  push({
                    sourceField: '',
                    targetField: '',
                    label: '',
                    inputType: '',
                    required: false,
                    order: writeback.fields.length,
                  })
                }
                disabled={!writeback?.tableName}
              />
              <FormErrorUI message={typeof writebackErrors?.fields === 'string' ? writebackErrors.fields : null} />
            </div>
            {writeback.fields.map((field, fieldIndex) => (
              <div className="grid grid-cols-12 gap-x-3 mb-4 items-center" key={field.id ?? fieldIndex}>
                <div className="col-span-2">
                  <Field
                    type="autocomplete"
                    name={`biWriteback.${index}.fields.${fieldIndex}.sourceField`}
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Riportmező" />}
                    variant="standard"
                    dataset={reportFieldOptions}
                    selectedValue={field.sourceField}
                    selectedLabelValue={field.sourceField}
                    helperText={<FormErrorUI message={writebackErrors?.fields?.[fieldIndex]?.sourceField} />}
                    onChange={(_event, selectedField) => {
                      const sourceField = selectedField?.value ?? '';
                      setFieldValue(
                        `biWriteback.${index}.fields.${fieldIndex}.sourceField`,
                        sourceField
                      )
                        .then(() => ensureReportField(sourceField))
                        .then(() =>
                          validateField(`biWriteback.${index}.fields.${fieldIndex}.sourceField`)
                        );
                    }}
                  />
                </div>
                <div className="col-span-2">
                  <Field
                    type="autocomplete"
                    name={`biWriteback.${index}.fields.${fieldIndex}.targetField`}
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Célmező" />}
                    variant="standard"
                    dataset={editableTargetFieldOptions}
                    selectedValue={field.targetField}
                    selectedLabelValue={field.targetField}
                    helperText={<FormErrorUI message={writebackErrors?.fields?.[fieldIndex]?.targetField} />}
                    onChange={(_event, selectedField) =>
                      setFieldValue(
                        `biWriteback.${index}.fields.${fieldIndex}.targetField`,
                        selectedField?.value ?? ''
                      )
                    }
                  />
                </div>
                <div className="col-span-3">
                  <Field
                    name={`biWriteback.${index}.fields.${fieldIndex}.label`}
                    as={InputUI}
                    fullWidth
                    label={<FormLabelUI text="Felirat" />}
                    variant="standard"
                    helperText={<FormErrorUI message={writebackErrors?.fields?.[fieldIndex]?.label} />}
                    InputLabelProps={{ shrink: Boolean(field.label) }}
                  />
                </div>
                <div className="col-span-3">
                  <Field
                    name={`biWriteback.${index}.fields.${fieldIndex}.inputType`}
                    component={SelectUI}
                    fullWidth
                    option={BiWritebackInputTypeList}
                    value={field.inputType ?? ''}
                    label={<FormLabelUI text="Beviteli típus" />}
                    variant="standard"
                    helperText={<FormErrorUI message={writebackErrors?.fields?.[fieldIndex]?.inputType} />}
                    onChange={(event) => {
                      setFieldValue(
                        `biWriteback.${index}.fields.${fieldIndex}.inputType`,
                        event.target.value
                      ).then(() => validateField(`biWriteback.${index}.fields.${fieldIndex}.inputType`));
                    }}
                  />
                </div>
                <div className="col-span-1">
                  <Field
                    name={`biWriteback.${index}.fields.${fieldIndex}.required`}
                    as={CheckboxUI}
                    label={<FormLabelUI text="Kötelező" />}
                    checked={Boolean(field.required)}
                    onChange={(event) =>
                      setFieldValue(`biWriteback.${index}.fields.${fieldIndex}.required`, event.target.checked)
                    }
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <button type="button" onClick={() => removeField(fieldIndex)} className="text-red-600">
                    <Delete />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </FieldArray>
    </div>
  );
};

const BiWritebackForm = ({ defaultField, autocompleteData }) => {
  const { values } = useFormikContext();
  const [tableOptions, setTableOptions] = useState([]);

  useEffect(() => {
    biWritebackService.getTables().then((tables) => {
      setTableOptions(
        tables.map((table) => ({
          ...table,
          value: table.tableName,
          label: table.label ?? table.tableName,
        }))
      );
    });
  }, []);

  const reportFieldOptions = useMemo(() => {
    const fields = autocompleteData.biField ?? [];

    return fields.filter((field, index) => fields.findIndex((item) => item.value === field.value) === index);
  }, [autocompleteData.biField]);

  return (
    <FieldArray name="biWriteback">
      {({ push, remove }) => (
        <>
          <div className="mx-4 mb-4 flex items-center gap-2">
            <Typography variant="h6">Visszaírási módok</Typography>
            <ButtonUI
              text={<Add fontSize="small" />}
              className="bg-success p-0.5"
              type="button"
              onClick={() =>
                push({
                  name: '',
                  tableName: '',
                  active: true,
                  order: values.biWriteback.length,
                  keys: [],
                  fields: [],
                })
              }
            />
          </div>
          {values.biWriteback.map((writeback, index) => (
            <BiWritebackConfiguration
              key={writeback.id ?? index}
              index={index}
              remove={remove}
              tableOptions={tableOptions}
              reportFieldOptions={reportFieldOptions}
              defaultField={defaultField}
            />
          ))}
        </>
      )}
    </FieldArray>
  );
};

export default BiWritebackForm;
